export type AudienceId =
  | "architects"
  | "engineers"
  | "developers"
  | "local-gov"
  | "mining"
  | "energy"
  | "contractors";

export interface Audience {
  id: AudienceId;
  name: string;
  /** One-line role tag under the name */
  short: string;
  /** Benefit-led headline */
  headline: string;
  /** Pain + how we help (2–3 sentences) */
  blurb: string;
  /** Specific pains this audience feels */
  pains: string[];
  /** How EcoEng addresses those pains */
  howWeHelp: string[];
  services: string[];
  bestTier: "DIY" | "Guided" | "Consultancy" | "Mix";
  /** Plain-English recommended start */
  startWith: string;
  ctaLabel: string;
  ctaTo: string;
  accent: string;
}

export const AUDIENCES: Audience[] = [
  {
    id: "architects",
    name: "Architects",
    short: "Design locked by late environmental surprises",
    headline: "Stop redesigning after the environmental constraints land.",
    blurb:
      "You’re juggling form, client ambition and council risk. Late ASS, marine or approvals issues force expensive redesign. We give you clear environmental constraints early — so design gets stronger, not delayed.",
    pains: [
      "Environmental issues appear after concept is already sold to the client",
      "DA pathways feel unclear until it’s too late to change massing or layout",
      "Reports arrive in a format that doesn’t help the design conversation",
    ],
    howWeHelp: [
      "Early constraint and pathway brief you can design against",
      "Guided approvals / soils / marine packs with ~48-hour turnaround options",
      "Co-branded outputs ready for client and council packs",
    ],
    services: [
      "Approvals strategy",
      "Soils & ASS",
      "Bushfire risk / BHMP",
      "WSUD",
      "Ecology & species ID",
      "Marine plants",
    ],
    bestTier: "Guided",
    startWith: "Guided report or free canvas for early framing",
    ctaLabel: "Get design-stage clarity",
    ctaTo: "/reports",
    accent: "#FF6B00",
  },
  {
    id: "engineers",
    name: "Engineers",
    short: "Time lost reinventing field tools and plans",
    headline: "Stop rebuilding the same checklists and plan shells every project.",
    blurb:
      "Civil, environmental and geotech teams waste hours on templates that aren’t field-ready — or wait months for a plan that should be standard. Grab proven tools now, or run a guided plan with our review in about 48 hours.",
    pains: [
      "Inconsistent templates across jobs and juniors",
      "ASS / ESC / soils plans that take too long to start",
      "Peer review bottleneck when the work is routine",
    ],
    howWeHelp: [
      "Instant DIY field sheets, canvases and plan starters",
      "Guided ASS, ESC and soil management plans with team review",
      "Technical language — no soft eco fluff",
    ],
    services: ["ASS", "ESC / CPESC", "Soils", "CEMP", "WSUD", "PMAV", "Regulatory"],
    bestTier: "DIY",
    startWith: "Resource library, then guided report if you need a signed-off PDF",
    ctaLabel: "Get field-ready tools",
    ctaTo: "/resources",
    accent: "#3D8BFF",
  },
  {
    id: "developers",
    name: "Developers",
    short: "Programme risk from environmental unknowns",
    headline: "Keep lots moving — without environmental surprises killing the programme.",
    blurb:
      "Every month of delay costs money. You need predictable environmental scope, clear pricing and deliverables your surveyors and planners can use. Start with tools or a guided report; only escalate to full consultancy when risk truly demands it.",
    pains: [
      "Unclear environmental cost and timing at feasibility",
      "Consultants over-scope simple pathways",
      "Subdivision packages hard to brief consistently",
    ],
    howWeHelp: [
      "Guided reports for standard pathways in ~48 hours",
      "Transparent pricing: DIY from free, guided from A$1,250, consultancy from A$7,500",
      "Urban package options (e.g. per-lot line) when full Tier 3 scope applies",
    ],
    services: [
      "Urban development",
      "ASS",
      "ESC",
      "PMAV / vegetation",
      "Offsets",
      "Bushfire",
      "Approvals",
    ],
    bestTier: "Guided",
    startWith: "Guided report for the critical pathway first",
    ctaLabel: "Protect your programme",
    ctaTo: "/reports",
    accent: "#FF6B00",
  },
  {
    id: "local-gov",
    name: "Local government",
    short: "Inconsistent applications and site controls",
    headline: "Raise application quality without rewriting the manual every time.",
    blurb:
      "Assessment teams see the same gaps: weak ESC, missing soils logic, unclear triggers. Share practical templates and checklists with industry — and escalate complex applications when you need specialist depth.",
    pains: [
      "Variable quality of DA and construction-stage environmental material",
      "Hard to resource detailed technical review on every file",
      "Need consistent language on professional limits of automated tools",
    ],
    howWeHelp: [
      "Council-ready DIY checklists and review canvases",
      "Clear professional-responsibility framing on every pack",
      "Specialist support when a file truly needs depth",
    ],
    services: ["ESC", "Regulatory", "WSUD", "Bushfire", "Ecology", "Soils", "Marine plants"],
    bestTier: "DIY",
    startWith: "Free and paid templates for assessment and site control",
    ctaLabel: "Browse council-ready tools",
    ctaTo: "/resources?category=regulatory",
    accent: "#2BB673",
  },
  {
    id: "mining",
    name: "Mining companies",
    short: "Disturbance, soils and audit pressure",
    headline: "Control disturbance and soils with documents that stand up to audit.",
    blurb:
      "Operational sites need soil handling, ESC and ASS logic that works at scale — and a paper trail regulators and auditors accept. Use structured guided packs for standard needs; bring us in fully when footprint and staging demand it.",
    pains: [
      "Large disturbed areas with inconsistent controls across sites",
      "Soil reuse / disposal decisions without a clear framework",
      "Audit and regulator scrutiny on documentation quality",
    ],
    howWeHelp: [
      "Guided soil management and ESC-oriented report packs",
      "Field tools for consistent site capture",
      "Standard Environmental Authority applications for mining (A$7,500.00 + GST) when that is the instrument",
    ],
    services: [
      "Soils",
      "ESC",
      "ASS",
      "CEMP / monitoring",
      "Offsets",
      "Approvals strategy",
      "Standard EA mining",
    ],
    bestTier: "Guided",
    startWith: "Guided soil or ESC pack",
    ctaLabel: "Start a site pack",
    ctaTo: "/reports",
    accent: "#C45C26",
  },
  {
    id: "energy",
    name: "Energy & infrastructure",
    short: "Multi-agency pathways on tight programmes",
    headline: "Sequence environmental risk so the corridor programme doesn’t stall.",
    blurb:
      "Linear assets cross jurisdictions, habitats and stakeholders. You need trigger mapping, marine/coastal interface support and deliverables that fit the programme — not open-ended studies by default.",
    pains: [
      "Approvals triggers spread across agencies and stages",
      "Coastal / marine interfaces poorly framed early",
      "Environmental work that doesn’t match construction sequencing",
    ],
    howWeHelp: [
      "Guided approvals strategy and marine plants packs",
      "Blue carbon and coastal framing tools for early options",
      "Consultancy only when complexity or conflict requires it",
    ],
    services: [
      "Approvals strategy",
      "Ecology & species ID",
      "Marine plants",
      "Blue carbon",
      "CEMP",
      "ESC",
      "Offsets",
    ],
    bestTier: "Guided",
    startWith: "Approvals strategy guided report",
    ctaLabel: "Map your pathway",
    ctaTo: "/reports",
    accent: "#5B7CFF",
  },
  {
    id: "contractors",
    name: "Contractors",
    short: "Controls that fail in the first storm",
    headline: "Get controls that survive the weather — and the site meeting.",
    blurb:
      "You’re judged on what holds up on site, not what looked fine in a PDF. Use practical field checklists and ESC tools today; escalate to a construction-stage guided plan when the risk is real.",
    pains: [
      "ESC and site controls that don’t match construction sequence",
      "No quick, defensible field sheets for crews",
      "Remediation cost when plans ignore real site conditions",
    ],
    howWeHelp: [
      "Instant DIY ESC and soils field tools",
      "Guided ESC plans reviewed for construction stages",
      "Upload photos and site context into your submission",
    ],
    services: ["ESC / CPESC", "CEMP", "Soils", "ASS field tools", "Pre-clearance", "Site checklists"],
    bestTier: "DIY",
    startWith: "ESC and field checklists in the library",
    ctaLabel: "Get tools for the crew",
    ctaTo: "/resources?category=esc",
    accent: "#FF6B00",
  },
];
